class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.addEventListener('click', this.handleDrawerClick.bind(this));
    this.addEventListener('submit', this.handleDrawerSubmit.bind(this));

    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    document.addEventListener('click', this.handleDocumentClick);

    this.floatingCart = document.querySelector('[data-cbm-floating-cart]');
    if (this.floatingCart) {
      this.floatingCart.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(this.floatingCart);
      });
    }

    this.updateFloatingCartPosition = this.updateFloatingCartPosition.bind(this);
    window.addEventListener('resize', this.updateFloatingCartPosition, { passive: true });
    window.addEventListener('scroll', this.updateFloatingCartPosition, { passive: true });
    requestAnimationFrame(this.updateFloatingCartPosition);

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      const count = event?.cartData?.item_count;
      if (typeof count === 'number') {
        this.syncFloatingCart(count);
        this.classList.toggle('is-empty', count === 0);
      } else {
        requestAnimationFrame(() => this.syncFloatingCartFromHeader());
      }
      requestAnimationFrame(() => this.setHeaderCartIconAccessibility());
    });

    this.setHeaderCartIconAccessibility();
    this.syncFloatingCartFromHeader();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
    window.removeEventListener('resize', this.updateFloatingCartPosition);
    window.removeEventListener('scroll', this.updateFloatingCartPosition);
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handleDrawerClick(event) {
    if (!event.target.closest('.drawer__inner')) {
      this.close();
      return;
    }

    const removeButton = event.target.closest('[data-cbm-remove-line]');
    if (removeButton) {
      event.preventDefault();
      const line = Number.parseInt(removeButton.dataset.cbmRemoveLine, 10);
      if (line > 0) this.removeLine(line, removeButton);
    }
  }

  handleDocumentClick(event) {
    if (!this.classList.contains('active')) return;
    if (event.target.closest('.drawer__inner')) return;
    if (event.target.closest('#cart-icon-bubble, [data-cbm-floating-cart]')) return;

    this.close();
  }

  handleDrawerSubmit(event) {
    const discountForm = event.target.closest('[data-cart-discount-form]');
    if (!discountForm) return;
    event.preventDefault();
    this.applyDiscount(discountForm);
  }

  updateFloatingCartPosition() {
    const header = document.querySelector('.section-header');
    const desktopNav = document.querySelector('.section-header .header__inline-menu');

    const headerRect = header?.getBoundingClientRect();
    const navRect = desktopNav?.getBoundingClientRect();
    const headerBottom = headerRect && headerRect.bottom > 0 ? headerRect.bottom : 0;
    const navBottom = navRect && navRect.height > 0 && navRect.bottom > 0 ? navRect.bottom : 0;
    const anchorBottom = Math.max(headerBottom, navBottom, 0);
    const drawerTop = Math.max(0, Math.min(window.innerHeight - 120, anchorBottom));

    document.documentElement.style.setProperty('--cbm-cart-drawer-top', `${drawerTop}px`);

    if (!this.floatingCart) return;
    const floatingTop = Math.max(12, drawerTop + 12);
    document.documentElement.style.setProperty('--cbm-floating-cart-top', `${floatingTop}px`);
  }

  syncFloatingCartFromHeader() {
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    const visibleCount = bubble?.querySelector('[aria-hidden="true"]')?.textContent || '';
    const accessibleCount = bubble?.querySelector('.visually-hidden')?.textContent || '';
    const countMatch = `${visibleCount} ${accessibleCount}`.match(/\d+/);
    const count = countMatch ? Number.parseInt(countMatch[0], 10) : 0;
    this.syncFloatingCart(count);
    this.classList.toggle('is-empty', count === 0);
    return count;
  }

  syncFloatingCart(count) {
    if (!this.floatingCart) return;
    const safeCount = Math.max(0, Number(count) || 0);
    const countElement = this.floatingCart.querySelector('[data-cbm-floating-cart-count]');
    const label = this.floatingCart.querySelector('[data-cbm-floating-cart-label]');

    if (countElement) countElement.textContent = safeCount > 99 ? '99+' : String(safeCount);
    if (label) label.textContent = `${safeCount} ${safeCount === 1 ? 'item' : 'items'} in cart`;
    this.floatingCart.hidden = safeCount === 0;
  }

  async removeLine(line, button) {
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }
    this.setDrawerNotice('', '');

    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          line,
          quantity: 0,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const cartState = await this.parseJsonResponse(response, 'Unable to update your cart right now. Please try again.');
      if (!response.ok || cartState.errors) {
        throw new Error(cartState.description || cartState.message || cartState.errors || 'Unable to remove this course.');
      }

      this.updateSections(cartState);
      this.syncFloatingCart(cartState.item_count);
      this.classList.toggle('is-empty', cartState.item_count === 0);
      this.emitCartState(cartState);

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cbm-cart-drawer',
        cartData: cartState,
      });

      if (cartState.item_count === 0) {
        requestAnimationFrame(() => this.open());
      }
    } catch (error) {
      if (button) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
      this.setDrawerNotice(error.message || 'Unable to update the cart right now.', 'error');
    }
  }

  async applyDiscount(form) {
    const input = form.querySelector('[name="discount"]');
    const button = form.querySelector('[type="submit"]');
    const code = (input?.value || '').trim();

    if (!code) {
      this.setDiscountStatus('Enter a discount code first.', 'error');
      input?.focus();
      return;
    }

    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Applying…';
    }
    this.setDiscountStatus('', '');

    try {
      const response = await fetch(window.routes.cart_update_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          discount: code,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });

      const cartState = await this.parseJsonResponse(response, 'Unable to apply that discount right now. Please try again.');
      if (!response.ok) throw new Error(cartState.description || cartState.message || 'Unable to apply discount.');

      const applied = this.discountWasApplied(cartState, code);
      this.updateSections(cartState);
      this.syncFloatingCart(cartState.item_count);
      this.classList.toggle('is-empty', cartState.item_count === 0);
      this.emitCartState(cartState);

      requestAnimationFrame(() => {
        const newInput = this.querySelector('[data-cart-discount-form] [name="discount"]');
        if (applied && newInput) newInput.value = '';
        this.setDiscountStatus(
          applied ? `Discount “${code}” applied.` : 'That code was not applied. Check the code or its eligibility.',
          applied ? 'success' : 'error'
        );
      });
    } catch (error) {
      this.setDiscountStatus(error.message || 'Unable to apply that discount right now.', 'error');
    } finally {
      const currentButton = this.querySelector('[data-cart-discount-form] [type="submit"]');
      if (currentButton) {
        currentButton.disabled = false;
        currentButton.removeAttribute('aria-busy');
        currentButton.textContent = 'Apply';
      }
    }
  }

  discountWasApplied(cartState, code) {
    const normalized = code.toLowerCase();
    const applications = cartState.cart_level_discount_applications || [];
    if (applications.some((application) => (application.title || '').toLowerCase() === normalized)) return true;

    return (cartState.items || []).some((item) =>
      (item.line_level_discount_allocations || []).some(
        (allocation) => (allocation.discount_application?.title || '').toLowerCase() === normalized
      )
    );
  }

  async parseJsonResponse(response, fallbackMessage) {
    const contentType = (response.headers.get('content-type') || '').toLowerCase();
    const raw = await response.text();

    if (!contentType.includes('application/json')) {
      console.warn('CE cart received a non-JSON response.', {
        status: response.status,
        url: response.url,
        contentType,
        preview: raw.slice(0, 140),
      });
      throw new Error(fallbackMessage);
    }

    try {
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      console.warn('CE cart received invalid JSON.', {
        status: response.status,
        url: response.url,
        preview: raw.slice(0, 140),
      });
      throw new Error(fallbackMessage);
    }
  }

  emitCartState(cartState) {
    document.dispatchEvent(
      new CustomEvent('cbm:cart-state', {
        detail: { cart: cartState },
      })
    );
  }

  setDiscountStatus(message, state) {
    const status = this.querySelector('[data-cart-discount-status]');
    if (!status) return;
    status.textContent = message;
    if (state) status.dataset.state = state;
    else delete status.dataset.state;
  }

  setDrawerNotice(message, state = 'info') {
    const notice = this.querySelector('[data-cbm-cart-notice]');
    if (!notice) return;
    notice.textContent = message || '';
    notice.hidden = !message;
    if (message && state) notice.dataset.state = state;
    else delete notice.dataset.state;
  }

  updateSections(parsedState) {
    if (!parsedState?.sections) return false;

    let updated = false;
    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (!html) return;

      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      const inner = this.getSectionInnerHTML(html, section.selector);
      if (typeof inner === 'string') {
        sectionElement.innerHTML = inner;
        updated = true;
      }
    });

    this.setHeaderCartIconAccessibility();
    this.updateFloatingCartPosition();
    return updated;
  }

  async refreshCart(triggeredBy, shouldOpen = true) {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const separator = root.includes('?') ? '&' : '?';
      const response = await fetch(`${root}${separator}sections=cart-drawer,cart-icon-bubble`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) throw new Error('Unable to refresh cart.');

      const sections = await this.parseJsonResponse(response, 'Unable to refresh your cart right now. Please try again.');
      this.updateSections({ sections });
      this.syncFloatingCartFromHeader();
      if (shouldOpen) this.open(triggeredBy);
      return true;
    } catch (error) {
      console.error(error);
      if (shouldOpen) this.open(triggeredBy);
      return false;
    }
  }

  async refreshAndOpen(triggeredBy) {
    return this.refreshCart(triggeredBy, true);
  }

  async refreshSilently(triggeredBy) {
    return this.refreshCart(triggeredBy, false);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cbmCartDrawerBound === 'true') return;
    cartLink.dataset.cbmCartDrawerBound = 'true';

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    this.updateFloatingCartPosition();
    requestAnimationFrame(() => this.classList.add('animate', 'active'));

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__close') || this.querySelector('.drawer__inner');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.updateFloatingCartPosition();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    requestAnimationFrame(() => {
      const count = this.syncFloatingCartFromHeader();
      this.updateFloatingCartPosition();
      if (this.wiggleAfterClose && count > 0) this.wiggleFloatingCart();
      this.wiggleAfterClose = false;
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling?.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  async renderContents(parsedState, options = {}) {
    this.productId = parsedState?.id;

    const shouldOpen = options.open !== false;
    const sourceElement = options.sourceElement || null;

    if (!this.updateSections(parsedState)) {
      if (shouldOpen) {
        this.wiggleAfterClose = true;
        await this.refreshAndOpen(sourceElement);
      } else {
        await this.refreshSilently(sourceElement);
        this.animateAddToFloatingCart(sourceElement);
      }
      return;
    }

    requestAnimationFrame(() => {
      this.syncFloatingCartFromHeader();
      this.updateFloatingCartPosition();

      if (shouldOpen) {
        this.wiggleAfterClose = true;
        this.open(sourceElement);
      } else {
        this.animateAddToFloatingCart(sourceElement);
      }
    });
  }

  animateAddToFloatingCart(sourceElement) {
    if (!this.floatingCart) return;

    const count = this.syncFloatingCartFromHeader();
    if (count <= 0) return;

    this.updateFloatingCartPosition();

    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || !sourceElement?.getBoundingClientRect) {
      this.wiggleFloatingCart();
      return;
    }

    const start = sourceElement.getBoundingClientRect();
    const target = this.floatingCart.getBoundingClientRect();

    if (!start.width || !start.height || !target.width || !target.height) {
      this.wiggleFloatingCart();
      return;
    }

    const startX = start.left + start.width / 2;
    const startY = start.top + start.height / 2;
    const endX = target.left + target.width / 2;
    const endY = target.top + target.height / 2;

    const flyer = document.createElement('span');
    flyer.className = 'cbm-cart-flyer';
    flyer.setAttribute('aria-hidden', 'true');
    flyer.innerHTML = `
      <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
        <path d="M7 8V6a5 5 0 0 1 10 0v2M5 8h14l1 12H4L5 8Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;
    document.body.appendChild(flyer);

    if (typeof window.requestAnimationFrame !== 'function') {
      flyer.remove();
      this.wiggleFloatingCart();
      return;
    }

    const horizontalDirection = Math.sign(endX - startX) || 1;

    // Small release pop, then one continuous Bezier curve all the way to the cart.
    // The control points intentionally keep the first half shallow and make the
    // curve progressively steeper near the cart, matching the requested arc.
    const popPoint = {
      x: startX + horizontalDirection * 14,
      y: startY - 18,
    };

    const dx = endX - popPoint.x;
    const dy = endY - popPoint.y;

    const curveStart = popPoint;
    const curveControl1 = {
      x: popPoint.x + dx * 0.46,
      y: popPoint.y + dy * 0.02,
    };
    const curveControl2 = {
      x: popPoint.x + dx * 0.88,
      y: popPoint.y + dy * 0.18,
    };
    const curveEnd = { x: endX, y: endY };

    const cubicPoint = (a, b, c, d, t) => {
      const mt = 1 - t;
      return (
        mt * mt * mt * a +
        3 * mt * mt * t * b +
        3 * mt * t * t * c +
        t * t * t * d
      );
    };

    const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
    const easeOutBack = (t) => {
      const c1 = 1.70158;
      const c3 = c1 + 1;
      return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    };

    const duration = 1120;
    const popEnd = 0.14;
    const startedAt = performance.now();
    let rafId = null;

    const cleanup = () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      flyer.remove();
    };

    const draw = (now) => {
      const rawProgress = Math.min(1, (now - startedAt) / duration);
      let x;
      let y;
      let opacity = 1;
      let scale = 1;
      let rotation = 0;

      if (rawProgress <= popEnd) {
        const t = rawProgress / popEnd;
        const moveEase = easeOutCubic(t);
        const scaleEase = easeOutBack(t);

        x = startX + (popPoint.x - startX) * moveEase;
        y = startY + (popPoint.y - startY) * moveEase;
        opacity = Math.min(1, t * 3.2);
        scale = 0.48 + scaleEase * 0.60;
        rotation = -7 + moveEase * 7;
      } else {
        const t = (rawProgress - popEnd) / (1 - popEnd);

        // Mild acceleration keeps the first part graceful and gives the final
        // approach more energy without creating a visible corner or 90-degree leg.
        const flightEase = Math.pow(t, 1.10);

        x = cubicPoint(curveStart.x, curveControl1.x, curveControl2.x, curveEnd.x, flightEase);
        y = cubicPoint(curveStart.y, curveControl1.y, curveControl2.y, curveEnd.y, flightEase);

        scale = 1.06 - flightEase * 0.62;
        rotation = Math.sin(t * Math.PI) * 2.5;
        opacity = t > 0.90 ? Math.max(0, (1 - t) / 0.10) : 1;
      }

      flyer.style.left = `${x}px`;
      flyer.style.top = `${y}px`;
      flyer.style.opacity = `${opacity}`;
      flyer.style.transform = `translate(-50%, -50%) scale(${scale}) rotate(${rotation}deg)`;

      if (rawProgress < 1) {
        rafId = window.requestAnimationFrame(draw);
        return;
      }

      cleanup();
      this.wiggleFloatingCart();
    };

    rafId = window.requestAnimationFrame(draw);
  }

  wiggleFloatingCart() {
    if (!this.floatingCart || this.floatingCart.hidden) return;
    this.floatingCart.classList.remove('is-wiggling');
    void this.floatingCart.offsetWidth;
    this.floatingCart.classList.add('is-wiggling');
    window.setTimeout(() => this.floatingCart?.classList.remove('is-wiggling'), 720);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
